import React, { Component } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Grid } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanAccount, ILoanComplianceCharge, ILoanPayment } from '../../../interfaces';
import { Icon, Loader } from '../../common';
import { SessionStoreClass } from '../../../stores';
import SmartBool from '../../../utils/SmartBool';

import { isChargeOpen } from './Charges';
import ComplianceChargesTable from './ComplianceChargesTable';
import CompliancePaymentsTable from './CompliancePaymentsTable';
import LogCompliancePaymentModal from './LogCompliancePaymentModal';

const { LOAN_PAYMENT_TYPE_COMPLIANCE, PERMISSIONS } = AppConstants;

interface IProps {
  loanAccount: ILoanAccount;
  // Logging a payment writes to the account's activity feed, which this tab doesn't own
  onPaymentLogged: () => void;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * The account's quarterly compliance charges and the payments settling them. Charges are opened by
 * a nightly job on the backend, so this tab only ever reads them -- the one thing it writes is a
 * payment against one.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class TabCompliance extends Component<IProps> {
  @observable private isLoading = true;
  @observable private charges: ILoanComplianceCharge[] = [];
  @observable private payments: ILoanPayment[] = [];
  @observable private showLogPaymentModal = new SmartBool();

  public componentDidMount () {
    this.fetchComplianceData();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchComplianceData ({ isRefresh = false } = {}) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    // A refresh leaves the tables up while it runs. Every logged payment goes through one, and
    // swapping both tables for a full-height loader makes the page jump for the round trip.
    this.isLoading = !isRefresh;

    try {
      // Both tables show everything on the account rather than paginating, as the loans table does.
      const [charges, payments] = await Promise.all([
        Clients.loanComplianceCharges.listAll({ params: { account: loanAccount.id } }),
        Clients.loanPayments.listAll({ params: { account: loanAccount.id, type: LOAN_PAYMENT_TYPE_COMPLIANCE } }),
      ]);

      this.charges = charges;
      this.payments = payments;
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the account compliance charges:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  private async handlePaymentLogged () {
    // Re-read rather than patching the row in place: the payment changes what the charge reports
    // as paid and outstanding, and the API is what works those out.
    await this.fetchComplianceData({ isRefresh: true });
    this.props.onPaymentLogged();
  }

  private get hasPayableCharge () {
    return this.charges.some(isChargeOpen);
  }

  /**
   * react-table wants plain arrays, not the observable ones mobx made of the responses.
   *
   * Cloned here rather than in each place they are handed down, so that the arrays keep the same
   * identity between renders. mobx-react holds props in an observable reference and only reports a
   * change when the incoming props are not shallow-equal to the last, so a stable array is what
   * lets the children's own @computed work skip rebuilding -- clone at the point of use and every
   * one of them is handed a new array on every parent render, and none of that memoisation holds.
   */
  @computed
  private get chargeData () {
    return toJS(this.charges);
  }

  @computed
  private get paymentData () {
    return toJS(this.payments);
  }

  private renderLogPaymentButton () {
    const { SessionStore } = this.injected;

    // Logging is rejected without the add permission, so only offer it to users who have it
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_PAYMENT)) { return null; }

    // Every compliance payment settles a charge, so there is nothing to log until one is open
    if (!this.hasPayableCharge) { return null; }

    return (
      // Not .btn-add: inside a well that class is for link-style buttons and drops the padding
      <Button bsStyle='primary' className='btn-log-compliance-payment' onClick={this.showLogPaymentModal.setTrue}>
        <Icon type='usd' /> Log payment
      </Button>
    );
  }

  private renderContent () {
    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    return (
      <div>
        <ComplianceChargesTable
          charges={this.chargeData}
          newButton={this.renderLogPaymentButton()}
        />
        <CompliancePaymentsTable charges={this.chargeData} payments={this.paymentData} />
      </div>
    );
  }

  public render () {
    const { loanAccount } = this.props;

    return (
      <Grid fluid className='loan-account-detail-tab tab-compliance'>
        {this.renderContent()}

        {this.showLogPaymentModal.isTrue && (
          <LogCompliancePaymentModal
            charges={this.chargeData}
            loanAccount={loanAccount}
            onClose={this.showLogPaymentModal.setFalse}
            onCreate={this.handlePaymentLogged}
          />
        )}
      </Grid>
    );
  }
}

export default TabCompliance;
